import { useState, useCallback, useEffect } from 'react';
import { useNotifications } from './useNotifications';
import { config } from '../config/environment';
import { getApiErrorMessage } from '../services/core/apiResponseHelpers';

// Hook para manejar notificaciones
export function useNotificationManager() {
  const {
    getAllNotifications,
    getNotificationCount,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();

  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false); // Control para evitar múltiples refreshes

  // Cargar todas las notificaciones
  const loadNotifications = useCallback(async () => {
    if (loading) return; // Evitar múltiples llamadas simultáneas

    setLoading(true);
    setError(null);

    try {
      const response = await getAllNotifications();

      if (response.success) {
        setNotifications(response.data);
      } else {
        setError(getApiErrorMessage(response, 'Error al cargar notificaciones'));
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Error de red';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [getAllNotifications, loading]);

  // Cargar conteo de notificaciones
  const loadNotificationCount = useCallback(async () => {
    try {
      const response = await getNotificationCount();

      if (response.success) {
        setUnreadCount(response.data.unread);
      } else {
        // Error silencioso para el conteo de notificaciones
      }
    } catch (error) {
      // Error silencioso para el conteo de notificaciones
    }
  }, [getNotificationCount]);

  // Cargar notificaciones al montar el componente
  useEffect(() => {
    loadNotifications();
  }, []); // Remover dependencias problemáticas

  // Cargar conteo de notificaciones al montar y actualizar cada 5 minutos
  useEffect(() => {
    loadNotificationCount();

    const interval = setInterval(
      loadNotificationCount,
      config.updates.notificationsInterval
    );
    return () => clearInterval(interval);
  }, []); // Remover dependencias problemáticas

  // Función para refrescar notificaciones con control de concurrencia
  const refreshNotifications = useCallback(async () => {
    if (isRefreshing) return; // Evitar múltiples refreshes simultáneos

    setIsRefreshing(true);
    try {
      await Promise.all([loadNotifications(), loadNotificationCount()]);
    } catch (error) {
      // Error silencioso para evitar loops de notificaciones
      console.warn('Error refreshing notifications:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing]); // Solo depende del estado de refreshing

  // Función para marcar como leída
  const handleMarkAsRead = useCallback(
    async (id: string) => {
      try {
        const response = await markAsRead(id);
        if (response.success) {
          await refreshNotifications();
        }
        return response;
      } catch (error) {
        console.error('Error marking notification as read:', error);
        return {
          success: false,
          data: null,
          error: 'Error al marcar como leída',
        };
      }
    },
    [markAsRead] // Remover refreshNotifications de dependencias
  );

  // Función para marcar como no leída
  const handleMarkAsUnread = useCallback(
    async (id: string) => {
      try {
        const response = await markAsUnread(id);
        if (response.success) {
          await refreshNotifications();
        }
        return response;
      } catch (error) {
        console.error('Error marking notification as unread:', error);
        return {
          success: false,
          data: null,
          error: 'Error al marcar como no leída',
        };
      }
    },
    [markAsUnread] // Remover refreshNotifications de dependencias
  );

  // Función para marcar todas como leídas
  const handleMarkAllAsRead = useCallback(
    async () => {
      try {
        const response = await markAllAsRead();
        if (response.success) {
          await refreshNotifications();
        }
        return response;
      } catch (error) {
        console.error('Error marking all notifications as read:', error);
        return {
          success: false,
          data: null,
          error: 'Error al marcar todas como leídas',
        };
      }
    },
    [markAllAsRead] // Remover refreshNotifications de dependencias
  );

  // Función para eliminar notificación
  const handleDeleteNotification = useCallback(
    async (id: string) => {
      try {
        const response = await deleteNotification(id);
        if (response.success) {
          await refreshNotifications();
        }
        return response;
      } catch (error) {
        console.error('Error deleting notification:', error);
        return {
          success: false,
          data: null,
          error: 'Error al eliminar notificación',
        };
      }
    },
    [deleteNotification] // Remover refreshNotifications de dependencias
  );

  return {
    notifications,
    unreadCount,
    loading,
    error,
    refreshNotifications,
    loadNotifications,
    loadNotificationCount,
    handleMarkAsRead,
    handleMarkAsUnread,
    handleMarkAllAsRead,
    handleDeleteNotification,
  };
}
